
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function Privacy() {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass fixed top-0 left-0 right-0 z-50 h-16 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <img src="/mishu_svg.svg" alt="mishu" className="h-8 w-auto" />
        </div>
        <Button asChild variant="ghost">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </Button>
      </header>

      <main className="flex-1 pt-24 px-6 pb-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last Updated: {currentDate}</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
            <p className="mb-4">
              We at mishu ("we," "us," or "our") respect your privacy and are committed to 
              protecting your personal data. This Privacy Policy explains how we collect, use, store, and 
              protect your information when you use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">2. Data Collection and Processing</h2>
            <p className="mb-4">
              We are committed to safeguarding your privacy. The Service collects and processes WhatsApp 
              messages only under the following conditions:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong>Short-Term Processing:</strong> Data is always anonymized and stored only temporarily 
                for processing before being deleted.
              </li>
              <li>
                <strong>Triggered Alerts Storage:</strong> Only messages that trigger an alert based on 
                predefined moderation criteria are stored for parental/guardian review.
              </li>
              <li>
                <strong>Data Deletion:</strong> When a user deletes a connection, all associated data is 
                permanently removed from our systems.
              </li>
              <li>
                <strong>Anonymization:</strong> Non-essential data is anonymized, and only relevant alerts 
                are retained for a limited duration.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">3. Data Protection & GDPR Compliance</h2>
            <p className="mb-4">
              We adhere to the principles of the General Data Protection Regulation (GDPR) and other 
              relevant privacy laws:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong>Lawful Basis for Processing:</strong> Data is processed based on parental/guardian 
                consent and legitimate interest.
              </li>
              <li>
                <strong>Data Minimization:</strong> Only necessary data is collected and stored.
              </li>
              <li>
                <strong>Right to Access & Deletion:</strong> Users can request access to their data or 
                request complete deletion at any time by contacting us.
              </li>
              <li>
                <strong>Security Measures:</strong> We implement industry-standard security protocols to 
                protect stored data.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">4. Third-Party Services</h2>
            <p className="mb-4">
              We do not share or sell user data to third parties. However, we may use third-party services 
              for hosting and data processing while ensuring they comply with our privacy standards.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">5. User Rights Under GDPR</h2>
            <p className="mb-4">As a user, you have the following rights:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Right to Access:</strong> Request a copy of the data we process about you.</li>
              <li><strong>Right to Rectification:</strong> Correct any inaccurate or incomplete data.</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your data.</li>
              <li><strong>Right to Restrict Processing:</strong> Limit how your data is processed.</li>
              <li><strong>Right to Data Portability:</strong> Request a copy of your data in a common format.</li>
              <li><strong>Right to Object:</strong> Object to data processing under certain circumstances.</li>
            </ul>
            <p className="mb-4">
              To exercise any of these rights, please contact us at 
              <a href="mailto:legal@knesis.com" className="text-primary hover:underline ml-1">
                legal@knesis.com
              </a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">6. Changes to This Privacy Policy</h2>
            <p className="mb-4">
              We reserve the right to update this Privacy Policy periodically. Users will be notified of any 
              material changes via email or through the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">7. Contact Information</h2>
            <p className="mb-4">If you have any questions regarding our Privacy Policy, please contact us at:</p>
            <p className="mb-4">Email: <a href="mailto:legal@knesis.com" className="text-primary hover:underline">legal@knesis.com</a></p>
          </section>

          <p className="italic text-muted-foreground">
            By using the Service, you confirm that you have read and agreed to this Privacy Policy and affirm that you have the lawful authority and legal right to access and monitor the data for the intended purposes of safeguarding and supervision.
          </p>
        </div>
      </main>

      <footer className="border-t py-6 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground mb-4 sm:mb-0">
            {new Date().getFullYear()} mishu. All rights reserved.
          </p>
          <div className="flex space-x-4">
            <Link
              to="/terms"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Privacy
            </Link>
            <a
              href="mailto:legal@knesis.com"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
